/**
 * Палитра команд (Cmd+K / Ctrl+K).
 * При вводе 3+ символов — параллельный поиск по данным (пилоты, аэропорты, самолёты, рейсы).
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  LayoutDashboard,
  Plane,
  Shield,
  Target,
  BarChart3,
  FileText,
  BookOpen,
  MapPin,
  Radar,
  Building2,
  Users,
  Lock,
  Settings,
  Search,
  User,
  Bell,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  suggestPilots,
  suggestAirports,
  suggestAircraft,
  getFlights,
} from '@/api/endpoints';
import type { SuggestItem } from '@/api/endpoints';
import { useAirportDisplayMode } from '@/hooks/useSystemSettings';
import { formatAirport } from '@/utils/formatAirport';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  path: string;
  keywords: string[];
}

const commands: CommandItem[] = [
  {
    id: 'dashboard',
    label: 'Главная',
    description: 'Дашборд с ключевыми метриками',
    icon: <LayoutDashboard size={18} />,
    path: '/dashboard',
    keywords: ['главная', 'дашборд', 'dashboard', 'home'],
  },
  {
    id: 'flights',
    label: 'Все полёты',
    description: 'Список полётов с фильтрацией',
    icon: <Plane size={18} />,
    path: '/flights',
    keywords: ['полёты', 'flights', 'рейсы'],
  },
  {
    id: 'safety-events',
    label: 'Все события',
    description: 'События безопасности полётов',
    icon: <Shield size={18} />,
    path: '/safety/events',
    keywords: ['события', 'безопасность', 'safety', 'events'],
  },
  {
    id: 'risk-map',
    label: 'Карта рисков',
    description: 'Матрица оценки рисков 5x5',
    icon: <Target size={18} />,
    path: '/safety/risk-map',
    keywords: ['карта', 'рисков', 'risk', 'map', 'матрица'],
  },
  {
    id: 'event-catalog',
    label: 'Каталог событий',
    description: 'Справочник типовых событий',
    icon: <BookOpen size={18} />,
    path: '/safety/catalog',
    keywords: ['каталог', 'событий', 'catalog'],
  },
  {
    id: 'analytics',
    label: 'Обзор',
    description: 'Аналитика и статистика',
    icon: <BarChart3 size={18} />,
    path: '/analytics',
    keywords: ['аналитика', 'обзор', 'analytics', 'статистика'],
  },
  {
    id: 'reports',
    label: 'Отчёты',
    description: 'Генерация и скачивание отчётов',
    icon: <FileText size={18} />,
    path: '/reports',
    keywords: ['отчёты', 'reports', 'генерация'],
  },
  {
    id: 'fleet',
    label: 'Парк ВС',
    description: 'Справочник воздушных судов',
    icon: <Plane size={18} />,
    path: '/fleet',
    keywords: ['парк', 'вс', 'fleet', 'самолёт', 'воздушные', 'суда'],
  },
  {
    id: 'airports',
    label: 'Аэропорты',
    description: 'Справочник аэропортов',
    icon: <MapPin size={18} />,
    path: '/airports',
    keywords: ['аэропорты', 'airports'],
  },
  {
    id: 'organization',
    label: 'Подразделения',
    description: 'Эскадрильи и подразделения',
    icon: <Building2 size={18} />,
    path: '/organization',
    keywords: ['подразделения', 'эскадрильи', 'organization'],
  },
  {
    id: 'pilots',
    label: 'Лётный состав',
    description: 'Справочник пилотов',
    icon: <Radar size={18} />,
    path: '/pilots',
    keywords: ['пилоты', 'лётный', 'состав', 'pilots'],
  },
  {
    id: 'users',
    label: 'Пользователи',
    description: 'Управление пользователями системы',
    icon: <Users size={18} />,
    path: '/admin/users',
    keywords: ['пользователи', 'users'],
  },
  {
    id: 'roles',
    label: 'Роли и права',
    description: 'Управление ролями и разрешениями',
    icon: <Lock size={18} />,
    path: '/admin/roles',
    keywords: ['роли', 'права', 'roles', 'permissions'],
  },
  {
    id: 'services',
    label: 'Службы',
    description: 'Управление службами организации',
    icon: <Building2 size={18} />,
    path: '/admin/services',
    keywords: ['службы', 'services'],
  },
  {
    id: 'settings',
    label: 'Настройки',
    description: 'Системные настройки',
    icon: <Settings size={18} />,
    path: '/admin/settings',
    keywords: ['настройки', 'settings'],
  },
  {
    id: 'profile',
    label: 'Профиль',
    description: 'Мой профиль',
    icon: <User size={18} />,
    path: '/profile',
    keywords: ['профиль', 'profile', 'мой'],
  },
  {
    id: 'notifications',
    label: 'Уведомления',
    description: 'Центр уведомлений',
    icon: <Bell size={18} />,
    path: '/notifications',
    keywords: ['уведомления', 'notifications'],
  },
];

// ── Data search result item ──────────────────────────────────

interface DataItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  path: string;
  group: string;
}

interface FlightItem {
  id: number;
  flight_number: string;
  flight_date: string;
  departure_airport_icao: string;
  arrival_airport_icao: string;
  aircraft_registration: string;
}

// ── useDebounce ──────────────────────────────────────────────

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer.current);
  }, [value, delay]);
  return debounced;
}

// ── CommandPalette ───────────────────────────────────────────

const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const airportMode = useAirportDisplayMode();

  const debouncedQuery = useDebounce(query, 300);
  const searchEnabled = debouncedQuery.trim().length >= 3;

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Parallel data queries (activate at 3+ chars) ──────────
  const pilotsQuery = useQuery<SuggestItem[]>({
    queryKey: ['cmd-pilots', debouncedQuery],
    queryFn: () => suggestPilots(debouncedQuery, 5),
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  const airportsQuery = useQuery<SuggestItem[]>({
    queryKey: ['cmd-airports', debouncedQuery],
    queryFn: () => suggestAirports(debouncedQuery, 5),
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  const aircraftQuery = useQuery<SuggestItem[]>({
    queryKey: ['cmd-aircraft', debouncedQuery],
    queryFn: () => suggestAircraft(debouncedQuery, 5),
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  const flightsQuery = useQuery<{ items: FlightItem[] }>({
    queryKey: ['cmd-flights', debouncedQuery],
    queryFn: () => getFlights({ q: debouncedQuery, per_page: 5 }),
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  const isSearching =
    searchEnabled &&
    (pilotsQuery.isFetching || airportsQuery.isFetching || aircraftQuery.isFetching || flightsQuery.isFetching);

  // Build data results
  const dataResults: DataItem[] = useMemo(() => {
    if (!searchEnabled) return [];
    const results: DataItem[] = [];

    (pilotsQuery.data ?? []).forEach((p) => {
      results.push({
        id: `pilot-${p.id}`,
        label: p.label,
        description: 'Пилот',
        icon: <Radar size={16} />,
        path: `/pilot-workspace/${p.id}`,
        group: 'Пилоты',
      });
    });

    (airportsQuery.data ?? []).forEach((a) => {
      results.push({
        id: `airport-${a.id}`,
        label: a.label,
        description: 'Аэропорт',
        icon: <MapPin size={16} />,
        path: `/airports/${a.id}`,
        group: 'Аэропорты',
      });
    });

    (aircraftQuery.data ?? []).forEach((a) => {
      results.push({
        id: `aircraft-${a.id}`,
        label: a.label,
        description: 'Воздушное судно',
        icon: <Plane size={16} />,
        path: `/fleet`,
        group: 'Воздушные суда',
      });
    });

    (flightsQuery.data?.items ?? []).forEach((f) => {
      results.push({
        id: `flight-${f.id}`,
        label: f.flight_number || `Рейс #${f.id}`,
        description: `${formatAirport({ icao_code: f.departure_airport_icao }, airportMode)} → ${formatAirport({ icao_code: f.arrival_airport_icao }, airportMode)} · ${f.aircraft_registration} · ${f.flight_date}`,
        icon: <Plane size={16} />,
        path: `/flights/${f.id}`,
        group: 'Полёты',
      });
    });

    return results;
  }, [searchEnabled, pilotsQuery.data, airportsQuery.data, aircraftQuery.data, flightsQuery.data]);

  // Filtered nav commands
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.description?.toLowerCase().includes(q) ||
        cmd.keywords.some((kw) => kw.includes(q)),
    );
  }, [query]);

  // Combined list for keyboard navigation
  const allItems = useMemo(() => [
    ...filtered.map((c) => ({ id: c.id, path: c.path })),
    ...dataResults.map((d) => ({ id: d.id, path: d.path })),
  ], [filtered, dataResults]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [allItems.length]);

  const handleSelect = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
      setQuery('');
    },
    [navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && allItems[selectedIndex]) {
        handleSelect(allItems[selectedIndex].path);
      } else if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    },
    [allItems, selectedIndex, handleSelect],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
        setQuery('');
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          mt: '15vh',
          alignSelf: 'flex-start',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Search size={20} style={{ color: theme.palette.text.secondary, marginRight: 12 }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Поиск разделов и данных..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: 15 }}
          />
          {isSearching ? (
            <CircularProgress size={16} sx={{ ml: 1, flexShrink: 0 }} />
          ) : (
            <Chip label="ESC" size="small" variant="outlined" sx={{ ml: 1, fontSize: 11, flexShrink: 0 }} />
          )}
        </Box>

        {/* Results */}
        <List sx={{ maxHeight: 480, overflowY: 'auto', py: 1 }}>
          {/* Navigation commands */}
          {filtered.length > 0 && (
            <>
              {searchEnabled && dataResults.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600 }}>
                  Разделы
                </Typography>
              )}
              {filtered.map((cmd, idx) => (
                <ListItemButton
                  key={cmd.id}
                  selected={idx === selectedIndex}
                  onClick={() => handleSelect(cmd.path)}
                  sx={{ mx: 1, borderRadius: 1, mb: 0.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: 'inherit' }}>
                    {cmd.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={cmd.label}
                    secondary={cmd.description}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              ))}
            </>
          )}

          {/* Data search results */}
          {searchEnabled && dataResults.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 0.5, display: 'block', fontWeight: 600 }}>
                Данные
              </Typography>
              {dataResults.map((item, idx) => {
                const globalIdx = filtered.length + idx;
                return (
                  <ListItemButton
                    key={item.id}
                    selected={globalIdx === selectedIndex}
                    onClick={() => handleSelect(item.path)}
                    sx={{ mx: 1, borderRadius: 1, mb: 0.25 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                    <Chip label={item.group} size="small" sx={{ ml: 1, fontSize: 10, height: 18, flexShrink: 0 }} />
                  </ListItemButton>
                );
              })}
            </>
          )}

          {/* No results */}
          {filtered.length === 0 && dataResults.length === 0 && !isSearching && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Ничего не найдено
              </Typography>
            </Box>
          )}

          {/* Hint to type more */}
          {query.trim().length > 0 && query.trim().length < 3 && (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Введите ещё {3 - query.trim().length} симв. для поиска по данным
              </Typography>
            </Box>
          )}
        </List>

        {/* Footer hint */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontSize: 10 }}>&uarr;&darr;</kbd> навигация
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontSize: 10 }}>Enter</kbd> перейти
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontSize: 10 }}>Esc</kbd> закрыть
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
